import { useState } from "react";
import PhotoUpload from "@/components/PhotoUpload";
import { Label } from "@/components/ui/label";
import { X, Star, GripVertical } from "lucide-react";

/**
 * Reorderable photo gallery editor (max 5). The FIRST photo is the hero — the
 * big 16:9 image at the top of the detail page. Owners can drag tiles to
 * reorder, tap the star to promote a photo to hero, or remove a photo. New
 * photos are added via the 16:9 cropping uploader and appended to the list.
 */
export default function PhotoGalleryEditor({ photos, onChange, max = 5, dragPaste = false, testid = "gallery" }) {
    const [dragIdx, setDragIdx] = useState(null);
    const list = (photos || []).filter(Boolean);
    const setList = (next) => onChange(next.slice(0, max));
    const removeAt = (i) => setList(list.filter((_, idx) => idx !== i));
    const makeHero = (i) => { const n = [...list]; const [x] = n.splice(i, 1); n.unshift(x); setList(n); };
    const move = (from, to) => {
        if (from == null || to == null || from === to) return;
        const n = [...list]; const [x] = n.splice(from, 1); n.splice(to, 0, x); setList(n);
    };
    const left = max - list.length;

    return (
        <div className="mt-3" data-testid={testid}>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Photos ({list.length}/{max})</Label>
            <div className="text-[10px] text-[#666] mt-0.5 mb-2">
                Drag to reorder. The <span className="text-[#ff4500]">first photo is the hero</span> (big image on the detail page). Up to {max}, all cropped 16:9.
            </div>
            {list.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {list.map((url, i) => (
                        <div
                            key={`${url}-${i}`}
                            draggable
                            onDragStart={() => setDragIdx(i)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { move(dragIdx, i); setDragIdx(null); }}
                            onDragEnd={() => setDragIdx(null)}
                            data-testid={`${testid}-tile-${i}`}
                            className={`relative border bg-[#141414] cursor-move transition-colors ${dragIdx === i ? "border-[#ff4500] opacity-60" : "border-[#2a2a2a]"}`}
                        >
                            <img src={url} alt={`Photo ${i + 1}`} className="w-full aspect-[16/9] object-cover bg-black" />
                            <GripVertical className="absolute bottom-1 left-1 h-4 w-4 text-white/60" />
                            {i === 0 && (
                                <span className="absolute top-1 left-1 bg-[#ff4500] text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5" data-testid={`${testid}-hero-badge`}>Hero</span>
                            )}
                            <div className="absolute top-1 right-1 flex gap-1">
                                {i !== 0 && (
                                    <button type="button" title="Make hero" data-testid={`${testid}-make-hero-${i}`} onClick={() => makeHero(i)} className="bg-black/70 p-1 hover:bg-[#ff4500] transition-colors">
                                        <Star className="h-3 w-3 text-white" />
                                    </button>
                                )}
                                <button type="button" title="Remove" data-testid={`${testid}-remove-${i}`} onClick={() => removeAt(i)} className="bg-black/70 p-1 hover:bg-red-600 transition-colors">
                                    <X className="h-3 w-3 text-white" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {left > 0 && (
                <div className="mt-2">
                    <PhotoUpload
                        value=""
                        onChange={(url) => { if (url) setList([...list, url]); }}
                        aspect={16 / 9}
                        label={list.length ? "Add another photo (16:9)" : "Add photo (16:9)"}
                        hint={`${left} slot${left > 1 ? "s" : ""} left · cropped to 16:9`}
                        dragPaste={dragPaste}
                        testid={`${testid}-add`}
                    />
                </div>
            )}
        </div>
    );
}
